import { useSelector } from 'react-redux';
import { usePreviousSlideIndex } from '@/features/client-onboarding/stores/onboarding-navigation-store';

import { selectClient, selectFeatureFlags } from '@/model';

import { ONBOARDING_SLIDES } from '@/features/client-onboarding/lib/slide-config';

export function useFundingCoverageSlide() {
  const client = useSelector(selectClient)!;
  const previousSlideIndex = usePreviousSlideIndex();
  const featureFlags = useSelector(selectFeatureFlags);

  const groupPolicyFeatureFlag = featureFlags.vbShop || featureFlags.agis;

  const {
    fundingSources: {
      combinedProjectedCostCoverage,
      combinedProjectedCostCoveragePercent,
      selfFunding: { hasSelfFunding },
    },
  } = client;

  const beginningText = determineBeginningText(
    client.fundingSources,
    groupPolicyFeatureFlag ?? false,
  );
  const showNoCoverageSelectedText = !combinedProjectedCostCoverage && groupPolicyFeatureFlag;

  // When previousSlideIndex is 0 (no history), the component should fall back to OnboardingFundingSources
  const previousSlide = previousSlideIndex === 0 ? null : ONBOARDING_SLIDES[previousSlideIndex];

  return {
    featureFlags,
    groupPolicyFeatureFlag,
    combinedProjectedCostCoverage,
    combinedProjectedCostCoveragePercent,
    beginningText,
    showNoCoverageSelectedText,
    hasSelfFunding,
    previousSlide,
  };
}

function determineBeginningText(fundingSources: FundingSources, groupPolicyFeatureFlag: boolean) {
  const {
    ltcPolicy: { hasPolicyFunding },
    selfFunding: { hasSelfFunding },
  } = fundingSources;

  const hasLtcPolicy = hasPolicyFunding === true;
  const hasOnlySelfFunding = hasSelfFunding && !hasLtcPolicy;
  const hasOnlyPolicyFunding = hasLtcPolicy && !hasSelfFunding;
  const hasBothFunding = hasSelfFunding && hasLtcPolicy;

  if (groupPolicyFeatureFlag) {
    return 'The option you selected';
  }

  if (hasOnlySelfFunding) {
    return 'Your self-funding plan';
  }

  if (hasOnlyPolicyFunding) {
    return 'Your policy';
  }

  if (hasBothFunding) {
    return 'Your policy and self-funding plan';
  }

  return 'Your current plan';
}
